// ============================================================================
// PostingEditPanel — panneau « Modifier l'affichage » (E1d, F-306)
// ============================================================================
// Portage maison du PoC posting-editor.tsx au standard fiche routee : panneau
// collapsible (details/summary, gabarit EditCommitteePanel E3a) plutot qu'un dialog
// — l'edition vit dans la fiche /affichages/:id. Champs M-300 : titre, description,
// taches (max 3), echeance. Mutation useUpdatePosting. Visible si viewer.canWrite
// (M-301, garde re-verifiee serveur). AUCUN upload de fichier (blob Firebase non
// migre) : la piece jointe existante se gere via le bloc metadonnees de la fiche
// (retrait uniquement).

import { useState } from 'react';
import { GS_COMPONENTS } from '@gs/ui';
import { useTranslation } from '../../i18n';
import { useUpdatePosting, postingErrorCode, type PostingDetail } from '../../hooks/usePostings';
import { TasksFields } from './TasksFields';


export function PostingEditPanel({
  posting,
  onError,
}: {
  posting: PostingDetail;
  onError: (code: string) => void;
}) {
  const { t } = useTranslation();
  const update = useUpdatePosting(posting.id);

  const [title, setTitle] = useState(posting.title);
  const [description, setDescription] = useState(posting.description ?? '');
  const [tasks, setTasks] = useState<string[]>(posting.tasks.length > 0 ? posting.tasks : ['']);
  const [deadline, setDeadline] = useState(posting.deadline ?? '');

  function save() {
    if (!title.trim()) {
      onError('invalid');
      return;
    }
    update.mutate(
      {
        title: title.trim(),
        description: description.trim() ? description.trim() : null,
        tasks: tasks.map((task) => task.trim()).filter((task) => task.length > 0).slice(0, 3),
        deadline: deadline || null,
      },
      { onError: (err) => onError(postingErrorCode(err)) },
    );
  }

  return (
    <details className={`${GS_COMPONENTS.card} group px-4 py-3`}>
      <summary className="cursor-pointer select-none text-field font-semibold text-slate-700 dark:text-slate-200">
        {t('gest:affichages.edit.summary')}
      </summary>
      <div className="mt-4 flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <label htmlFor="edit-posting-title" className="text-meta font-medium text-slate-600 dark:text-slate-300">
            {t('gest:affichages.form.postTitle')}
          </label>
          <input
            id="edit-posting-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={200}
            className={`${GS_COMPONENTS.input}`}
          />
        </div>
        <div className="flex flex-col gap-2">
          <label htmlFor="edit-posting-desc" className="text-meta font-medium text-slate-600 dark:text-slate-300">
            {t('gest:affichages.form.description')}
          </label>
          <textarea
            id="edit-posting-desc"
            rows={4}
            maxLength={2000}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className={`${GS_COMPONENTS.input}`}
          />
        </div>
        <TasksFields tasks={tasks} onChange={setTasks} />
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="edit-posting-deadline" className="text-meta font-medium text-slate-600 dark:text-slate-300">
              {t('gest:affichages.form.deadline')}
            </label>
            <input
              id="edit-posting-deadline"
              type="date"
              value={deadline}
              onChange={(e) => setDeadline(e.target.value)}
              className={`${GS_COMPONENTS.input} w-48`}
            />
          </div>
          <button type="button" onClick={save} disabled={update.isPending} className={GS_COMPONENTS.btnAction}>
            {t('gest:affichages.edit.save')}
          </button>
        </div>
      </div>
    </details>
  );
}
